const configuredBase = import.meta.env.VITE_API_URL as string | undefined;
export const apiBaseUrl = (configuredBase?.trim() || "/api").replace(/\/$/, "");

export const AUTHORIZATION_REQUIRED_EVENT = "trading:authorization-required";
export const API_REQUEST_FAILED_EVENT = "trading:api-request-failed";

export interface ApiRequestFailure {
  path: string;
  method: string;
  status: number;
  message: string;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const response = await fetch(`${apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`, {
    ...init,
    method,
    headers,
    credentials: init.credentials ?? "include",
  });
  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = text;
  }
  if (!response.ok) {
    const message = body && typeof body === "object" && "message" in body
      ? String((body as { message: unknown }).message)
      : text || `${response.status} ${response.statusText}`;
    if (response.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event(AUTHORIZATION_REQUIRED_EVENT));
    }
    const actionableMessage = response.status === 401
      ? "Your secure session is missing or expired. Sign in again."
      : message;
    if (response.status !== 401 && method === "GET" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent<ApiRequestFailure>(API_REQUEST_FAILED_EVENT, {
        detail: { path, method, status: response.status, message: actionableMessage },
      }));
    }
    const error = new Error(actionableMessage) as Error & { statusCode?: number; response?: unknown; authorizationRequired?: boolean };
    error.statusCode = response.status;
    error.response = body;
    error.authorizationRequired = response.status === 401;
    throw error;
  }
  return body as T;
}

// Remove the legacy raw-token cache. Authentication now uses an HttpOnly cookie only.
if (typeof window !== "undefined") window.sessionStorage.removeItem("tradingAdminToken");
